import { useState, useRef } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import { useAuthz } from "context/AuthzContext";
import { useCompany } from "context/CompanyContext";
import { backupTenant, restoreTenant } from "services/tenantUserAdminService";
import { TC } from "../../tokens/text";

function BackupRestore() {
  const { tenantId, isSuperAdmin, isTenantAdmin } = useAuthz();
  const { companies } = useCompany();

  const [backupLoading, setBackupLoading] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(null);
  const [message, setMessage] = useState("");
  const restoreInputRef = useRef(null);
  const [restoreTargetId, setRestoreTargetId] = useState(null);

  const tenants = isSuperAdmin
    ? Array.isArray(companies) ? companies : []
    : Array.isArray(companies)
      ? companies.filter((c) => c.id === tenantId)
      : [];

  const handleBackup = async (org) => {
    setBackupLoading(org.id);
    setMessage("");
    try {
      const data = await backupTenant(org.id);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(org.name || org.id).replace(/\s+/g, "-")}-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setMessage(`Backup of "${org.name}" downloaded successfully.`);
      setTimeout(() => setMessage(""), 5000);
    } catch (err) {
      setMessage(`Error: ${err?.message || "Backup failed."}`);
      setTimeout(() => setMessage(""), 5000);
    } finally {
      setBackupLoading(null);
    }
  };

  const handleRestoreClick = (org) => {
    setRestoreTargetId(org.id);
    restoreInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !restoreTargetId) return;
    setRestoreLoading(restoreTargetId);
    setMessage("");
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      await restoreTenant(restoreTargetId, parsed);
      const name = tenants.find((t) => t.id === restoreTargetId)?.name || restoreTargetId;
      setMessage(`Restore to "${name}" completed successfully.`);
      setTimeout(() => setMessage(""), 5000);
    } catch (err) {
      setMessage(`Error: ${err?.message || "Restore failed."}`);
      setTimeout(() => setMessage(""), 5000);
    } finally {
      setRestoreLoading(null);
      setRestoreTargetId(null);
      e.target.value = "";
    }
  };

  if (!isSuperAdmin && !isTenantAdmin) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox py={3}>
          <VuiTypography color="warning" variant="button">Only admins can access Backup &amp; Restore.</VuiTypography>
        </VuiBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiTypography variant="lg" sx={{ color: TC.primary }} mb={3}>
          Backup &amp; Restore
        </VuiTypography>

        {message && (
          <VuiTypography
            variant="button"
            mb={2}
            display="block"
            sx={{ color: message.startsWith("Error") ? "#f44336" : "#4caf50" }}
          >
            {message}
          </VuiTypography>
        )}

        <Grid container spacing={2}>
          {tenants.map((org) => (
            <Grid item xs={12} md={6} key={org.id}>
              <Card>
                <VuiBox p={3}>
                  <VuiTypography variant="button" fontWeight="bold" sx={{ color: TC.primary }} display="block" mb={0.5}>
                    {org.name || org.id}
                  </VuiTypography>
                  <VuiTypography variant="caption" sx={{ color: TC.secondary }} display="block" mb={2}>
                    Tenant ID: {org.id}
                  </VuiTypography>

                  <VuiBox display="flex" gap={2} flexWrap="wrap">
                    <VuiButton
                      color="info"
                      size="small"
                      disabled={backupLoading === org.id}
                      onClick={() => handleBackup(org)}
                    >
                      {backupLoading === org.id ? "Backing up..." : "Download Backup"}
                    </VuiButton>
                    <VuiButton
                      color="warning"
                      size="small"
                      disabled={restoreLoading === org.id}
                      onClick={() => handleRestoreClick(org)}
                    >
                      {restoreLoading === org.id ? "Restoring..." : "Restore from File"}
                    </VuiButton>
                  </VuiBox>
                </VuiBox>
              </Card>
            </Grid>
          ))}
        </Grid>

        <input
          type="file"
          accept=".json"
          ref={restoreInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default BackupRestore;

